import { lazy, Suspense, useEffect } from "react";
import { MotionConfig } from "framer-motion";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Dashboard from "@/pages/Dashboard";
import TourHost from "@/components/TourHost";
import GlobalKeys from "@/components/GlobalKeys";
import { preloadRichEditor } from "@/components/RichEditor";
import { startCrumbs, routeCrumbs } from "@/lib/crumbs";

startCrumbs();

const load = {
  KidHome: () => import("@/pages/KidHome"),
  ResetPage: () => import("./pages/ResetPage"),
  AuthPage: () => import("@/pages/AuthPage"),
  TasksPage: () => import("@/pages/TasksPage"),
  PropagationPage: () => import("@/pages/PropagationPage"),
  TeamPage: () => import("@/pages/TeamPage"),
  MemberPage: () => import("@/pages/MemberPage"),
  SettingsPage: () => import("@/pages/SettingsPage"),
  PlantDetail: () => import("@/pages/PlantDetail"),
  CasePage: () => import("@/pages/CasePage"),
  LabelsPage: () => import("@/pages/LabelsPage"),
  BatchCare: () => import("@/pages/BatchCare"),
  UpdatesPage: () => import("@/pages/UpdatesPage"),
  PropagationDetail: () => import("@/pages/PropagationDetail"),
  PhotoLibrary: () => import("@/pages/PhotoLibrary"),
};
const P = Object.fromEntries(Object.entries(load).map(([k, f]) => [k, lazy(f)]));

// Warm the most-visited pages in the background once the first screen is idle.
function usePrefetch(user) {
  useEffect(() => {
    if (!user) return undefined;
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
    const id = idle(() => {
      const list = user.role === "kid" ? [load.KidHome] : [load.PlantDetail, preloadRichEditor, load.TasksPage, load.CasePage, load.SettingsPage, load.BatchCare];
      list.reduce((p, f) => p.then(() => f().catch(() => {})), Promise.resolve());
    });
    return () => (window.cancelIdleCallback ? window.cancelIdleCallback(id) : clearTimeout(id));
  }, [user]);
}

function RouteFade({ children }) {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  useEffect(() => { if (navType !== "POP") window.scrollTo(0, 0); routeCrumbs(pathname); }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return <div key={pathname.split("/")[1]} className="page-in">{children}</div>;
}

function KidGate({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  usePrefetch(user);
  if (user?.role !== "kid") return children;
  return pathname === "/" ? <P.KidHome /> : <Navigate to="/" replace />;
}

const guard = (el) => <ProtectedRoute>{el}</ProtectedRoute>;

function App() {
  return (
    <div className="App">
      <MotionConfig reducedMotion="user" transition={{ type: "spring", stiffness: 380, damping: 32 }}>
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<div className="min-h-[60vh]" data-testid="page-loading" />}>
          <KidGate>
          <RouteFade>
          <Routes>
            <Route path="/login" element={<P.AuthPage />} />
            <Route path="/reset/:token" element={<P.ResetPage />} />
            <Route path="/" element={guard(<Dashboard />)} />
            <Route path="/plants/:id" element={guard(<P.PlantDetail />)} />
            <Route path="/settings" element={guard(<P.SettingsPage />)} />
            <Route path="/cases/:id" element={guard(<P.CasePage />)} />
            <Route path="/labels" element={guard(<P.LabelsPage />)} />
            <Route path="/batch" element={guard(<P.BatchCare />)} />
            <Route path="/tasks" element={guard(<P.TasksPage />)} />
            <Route path="/propagation" element={guard(<P.PropagationPage />)} />
            <Route path="/propagation/:id" element={guard(<P.PropagationDetail />)} />
            <Route path="/photos" element={guard(<P.PhotoLibrary />)} />
            <Route path="/team" element={guard(<P.TeamPage />)} />
            <Route path="/me" element={guard(<P.MemberPage />)} />
            <Route path="/members/:id" element={guard(<P.MemberPage />)} />
            <Route path="/updates" element={guard(<P.UpdatesPage />)} />
          </Routes>
          </RouteFade>
          <TourHost />
          <GlobalKeys />
          </KidGate>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
      </MotionConfig>
      <Toaster richColors position="bottom-right" mobileOffset={{ bottom: "calc(5.5rem + env(safe-area-inset-bottom))" }} />
    </div>
  );
}

export default App;
